import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import {
  CodingStrategyEnum,
  OrganizationIdentifierKindEnum,
  ThirdPartyTypeEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';
import { formatOrganizationIdentifierInput } from '@e-socna/third-parties';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type { ThirdPartyForm_Organization } from '../fragments/organization.fragment';

interface Params {
  readonly isInternal: boolean;
  readonly initialValues?: Partial<ThirdPartyForm_Organization> | null;
}

export const useOrganizationInitialValuesFactory = ({
  isInternal,
  initialValues: pInitialValues,
}: Readonly<Params>) => {
  const {
    thirdPartyTypesCodeMap,
    genericStatusesCodeMap,
    codingStrategiesCodeMap,
  } = useReferencesContext();
  const { generateId } = useIdFactory();

  const initialValues: ThirdPartyForm_Organization = useMemo(
    () => ({
      __typename: ThirdPartyTypeEnum.Organization as const,
      _id: generateId(),
      code: EMPTY_STRING,
      isVip: false,
      addresses: EMPTY_ARRAY,
      color: EMPTY_STRING,
      vatNumber: EMPTY_STRING,
      name: EMPTY_STRING,
      tradingName: EMPTY_STRING,
      billEmitterIds: EMPTY_ARRAY,
      childIds: EMPTY_ARRAY,
      birthDate: EMPTY_STRING,
      memo: EMPTY_STRING,
      internalMemo: EMPTY_STRING,
      activity: EMPTY_STRING,
      logoImageUri: EMPTY_STRING,
      ...omitBy(pInitialValues, isNil),
      activeMemberships: pInitialValues?.activeMemberships ?? EMPTY_ARRAY,
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigAutomatic as const,
        _id: generateId(),
        kind: CodingStrategyEnum.CodingConfigAutomatic,
        strategyId: codingStrategiesCodeMap.CodingConfigAutomatic._id,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      contactInfo: {
        __typename: 'ContactInfo' as const,
        _id: generateId(),
        email: EMPTY_STRING,
        mobileNumber: EMPTY_STRING,
        landlineNumber: EMPTY_STRING,
        professionalNumber: EMPTY_STRING,
        faxNumber: EMPTY_STRING,
        website: EMPTY_STRING,
        ...omitBy(pInitialValues?.contactInfo, isNil),
      },
      bankInfo: {
        __typename: 'BankInfo' as const,
        _id: generateId(),
        accountOwner: EMPTY_STRING,
        bankCode: EMPTY_STRING,
        branchCode: EMPTY_STRING,
        accountNumber: EMPTY_STRING,
        certificateKey: EMPTY_STRING,
        iban: EMPTY_STRING,
        swift: EMPTY_STRING,
        domiciliation: EMPTY_STRING,
        ...omitBy(pInitialValues?.bankInfo, isNil),
      },
      shareCapital: {
        __typename: 'ShareCapital' as const,
        _id: generateId(),
        currencyId: EMPTY_ID,
        value: EMPTY_STRING as unknown as number,
        ...omitBy(pInitialValues?.shareCapital, isNil),
      },
      categoryIds: pInitialValues?.categoryIds ?? EMPTY_ARRAY,
      identifier: {
        __typename: 'OrganizationIdentifier' as const,
        _id: generateId(),
        kind: OrganizationIdentifierKindEnum.Siren,
        ...omitBy(pInitialValues?.identifier, isNil),
        value: pInitialValues?.identifier?.value
          ? formatOrganizationIdentifierInput(pInitialValues.identifier.value)
          : EMPTY_STRING,
      },
      statusId: pInitialValues?.statusId ?? genericStatusesCodeMap.Enabled._id,
      billReceiverId: pInitialValues?.billReceiverId ?? EMPTY_ID,
      legalStatusId: pInitialValues?.legalStatusId ?? EMPTY_ID,
      motherId: pInitialValues?.motherId ?? EMPTY_ID,
      nafId: pInitialValues?.nafId ?? EMPTY_ID,
      isInternal,
      kind: thirdPartyTypesCodeMap.Organization.code,
      typeId: thirdPartyTypesCodeMap.Organization._id,
    }),
    [
      generateId,
      genericStatusesCodeMap.Enabled._id,
      isInternal,
      pInitialValues,
      thirdPartyTypesCodeMap.Organization._id,
      thirdPartyTypesCodeMap.Organization.code,
      codingStrategiesCodeMap.CodingConfigAutomatic._id,
    ],
  );

  return { initialValues };
};
